import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessInvoices = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : (outletContext?.canManageInvoices ?? true);
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadInvoices = async () => {
    try {
      let url = `${apiBase}/invoices`;
      if (filter !== 'all') url += `?statut=${filter}`;
      const res = await api.get(url);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await api.delete(`${apiBase}/invoices/${id}`);
      loadInvoices();
    } catch (err) {
      console.error(err);
    }
  };

  const handleValidate = async (id, numeroFacture) => {
    if (!window.confirm(`Valider la facture ${numeroFacture} ?\n\nCette action va :\n• Changer le statut en "Envoyée"\n• Déduire les quantités du stock des produits`)) return;
    try {
      await api.post(`${apiBase}/invoices/${id}/validate`);
      alert('Facture validée ! Les stocks ont été mis à jour.');
      loadInvoices();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la validation');
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-gray-100 text-gray-600',
      envoyee: 'bg-blue-100 text-blue-600',
      partiellement_payee: 'bg-amber-100 text-amber-600',
      payee: 'bg-green-100 text-green-600',
      annulee: 'bg-red-100 text-red-600'
    };
    const labels = {
      brouillon: 'Brouillon',
      envoyee: 'Envoyée',
      partiellement_payee: 'Partiel',
      payee: 'Payée',
      annulee: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[statut] || styles.brouillon}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredInvoices = invoices.filter(invoice => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      invoice.numero_facture?.toLowerCase().includes(searchLower) ||
      invoice.client_nom?.toLowerCase().includes(searchLower) ||
      invoice.client_telephone?.includes(search) ||
      invoice.client_email?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const stats = {
    total: invoices.length,
    brouillon: invoices.filter(i => i.statut === 'brouillon').length,
    impayees: invoices.filter(i => ['envoyee', 'partiellement_payee'].includes(i.statut)).length,
    payees: invoices.filter(i => i.statut === 'payee').length,
    montantImpaye: invoices
      .filter(i => ['envoyee', 'partiellement_payee'].includes(i.statut))
      .reduce((sum, i) => sum + (parseFloat(i.total) - parseFloat(i.montant_paye || 0)), 0)
  };

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Factures</h1>
          <p className="text-gray-500 text-xs sm:text-sm">Gérez vos factures clients</p>
        </div>
        {canManage && (
          <Link
            to={`${routeBase}/invoices/new`}
            className="px-3 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto"
          >
            <span>➕</span> Nouvelle facture
          </Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 md:gap-4 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-gray-500 text-lg sm:text-2xl mb-1 sm:mb-2">📄</div>
          <div className="text-lg sm:text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-xs sm:text-sm text-gray-500">Total</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-gray-400 text-lg sm:text-2xl mb-1 sm:mb-2">📝</div>
          <div className="text-lg sm:text-2xl font-bold text-gray-600">{stats.brouillon}</div>
          <div className="text-xs sm:text-sm text-gray-500">Brouillons</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-amber-500 text-lg sm:text-2xl mb-1 sm:mb-2">⏳</div>
          <div className="text-lg sm:text-2xl font-bold text-amber-600">{stats.impayees}</div>
          <div className="text-xs sm:text-sm text-gray-500">À encaisser</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-red-500 text-lg sm:text-2xl mb-1 sm:mb-2">💰</div>
          <div className="text-base sm:text-xl font-bold text-red-600">{stats.montantImpaye.toLocaleString()}</div>
          <div className="text-xs sm:text-sm text-gray-500">Impayé (XOF)</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-2.5 sm:p-4 mb-4 sm:mb-6">
        {/* Barre de recherche */}
        <div className="mb-3 sm:mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 sm:pl-10 pr-8 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-2.5 px-2.5 sm:mx-0 sm:px-0">
          {[
            { value: 'all', label: 'Toutes', mobileLabel: 'Tout' },
            { value: 'brouillon', label: '📝 Brouillons', mobileLabel: '📝' },
            { value: 'envoyee', label: '📤 Envoyées', mobileLabel: '📤' },
            { value: 'partiellement_payee', label: '⏳ Partielles', mobileLabel: '⏳' },
            { value: 'payee', label: '✅ Payées', mobileLabel: '✅' },
            { value: 'annulee', label: '❌ Annulées', mobileLabel: '❌' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition flex-shrink-0 ${
                filter === f.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{f.label}</span>
              <span className="sm:hidden">{f.mobileLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-500">
          {filteredInvoices.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Invoices List */}
      {paginatedInvoices.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">N° Facture</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Payé</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Statut</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-indigo-600">{invoice.numero_facture}</div>
                      {invoice.type === 'proforma' && (
                        <span className="text-xs text-gray-400">Proforma</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{invoice.client_nom || 'Client anonyme'}</div>
                      {invoice.client_telephone && (
                        <div className="text-xs text-gray-400">{invoice.client_telephone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(invoice.date_facture).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">
                      {parseFloat(invoice.total).toLocaleString()} XOF
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={parseFloat(invoice.montant_paye) >= parseFloat(invoice.total) ? 'text-green-600' : 'text-gray-600'}>
                        {parseFloat(invoice.montant_paye || 0).toLocaleString()} XOF
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(invoice.statut)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        <Link
                          to={`${routeBase}/invoices/${invoice.id}`}
                          className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                          title="Voir"
                        >
                          👁️
                        </Link>
                        {canManage && invoice.statut === 'brouillon' && (
                          <>
                            <button
                              onClick={() => handleValidate(invoice.id, invoice.numero_facture)}
                              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                              title="Valider et mettre à jour le stock"
                            >
                              ✅
                            </button>
                            <Link
                              to={`${routeBase}/invoices/${invoice.id}/edit`}
                              className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                              title="Modifier"
                            >
                              ✏️
                            </Link>
                          </>
                        )}
                        {canManage && invoice.statut !== 'payee' && invoice.statut !== 'brouillon' && (
                          <Link
                            to={`${routeBase}/payments/new?invoice_id=${invoice.id}`}
                            className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                            title="Encaisser"
                          >
                            💰
                          </Link>
                        )}
                        {canManage && invoice.statut === 'brouillon' && (
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2.5">
            {paginatedInvoices.map(invoice => (
              <div key={invoice.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header de la carte */}
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-indigo-600 text-sm">{invoice.numero_facture}</div>
                      <div className="text-xs font-medium text-gray-800 truncate">{invoice.client_nom || 'Client anonyme'}</div>
                      {invoice.client_telephone && (
                        <div className="text-xs text-gray-500">📞 {invoice.client_telephone}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      {getStatusBadge(invoice.statut)}
                      <span className="text-xs text-gray-400">{new Date(invoice.date_facture).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Montants */}
                <div className="p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-sm font-bold text-gray-900">{parseFloat(invoice.total).toLocaleString()} <span className="text-xs font-normal text-gray-500">XOF</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Payé</div>
                      <div className={`text-sm font-bold ${parseFloat(invoice.montant_paye) >= parseFloat(invoice.total) ? 'text-green-600' : 'text-amber-600'}`}>
                        {parseFloat(invoice.montant_paye || 0).toLocaleString()} <span className="text-xs font-normal">XOF</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Reste à payer */}
                  {parseFloat(invoice.montant_paye || 0) < parseFloat(invoice.total) && invoice.statut !== 'brouillon' && invoice.statut !== 'annulee' && (
                    <div className="bg-red-50 rounded-lg px-2 py-1.5 mb-2 flex justify-between items-center">
                      <div className="text-xs text-red-600">Reste</div>
                      <div className="font-bold text-red-700 text-sm">{(parseFloat(invoice.total) - parseFloat(invoice.montant_paye || 0)).toLocaleString()} XOF</div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-1.5 flex-wrap">
                    <Link
                      to={`${routeBase}/invoices/${invoice.id}`}
                      className="flex-1 min-w-[60px] py-2 px-2 bg-indigo-50 text-indigo-600 rounded-lg text-center font-medium text-xs hover:bg-indigo-100 transition"
                    >
                      👁️ Voir
                    </Link>
                    {canManage && invoice.statut === 'brouillon' && (
                      <>
                        <button
                          onClick={() => handleValidate(invoice.id, invoice.numero_facture)}
                          className="flex-1 min-w-[60px] py-2 px-2 bg-blue-50 text-blue-600 rounded-lg text-center font-medium text-xs hover:bg-blue-100 transition"
                        >
                          ✅ Valider
                        </button>
                        <Link
                          to={`${routeBase}/invoices/${invoice.id}/edit`}
                          className="py-2 px-3 bg-amber-50 text-amber-600 rounded-lg font-medium text-xs hover:bg-amber-100 transition"
                        >
                          ✏️
                        </Link>
                        <button
                          onClick={() => handleDelete(invoice.id)}
                          className="py-2 px-3 bg-red-50 text-red-600 rounded-lg font-medium text-xs hover:bg-red-100 transition"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                    {canManage && invoice.statut !== 'payee' && invoice.statut !== 'brouillon' && invoice.statut !== 'annulee' && (
                      <Link
                        to={`${routeBase}/payments/new?invoice_id=${invoice.id}`}
                        className="flex-1 min-w-[60px] py-2 px-2 bg-green-50 text-green-600 rounded-lg text-center font-medium text-xs hover:bg-green-100 transition"
                      >
                        💰 Encaisser
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-3 sm:mt-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-t rounded-b-xl flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
              <div className="text-xs sm:text-sm text-gray-500">
                {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredInvoices.length)} sur {filteredInvoices.length}
              </div>
              <div className="flex gap-0.5 sm:gap-1 flex-wrap justify-center">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  «
                </button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  
                  // Toujours afficher la première et dernière page
                  showPages.add(1);
                  showPages.add(totalPages);
                  
                  // Pages autour de la page courante
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  
                  sortedPages.forEach((page, idx) => {
                    // Ajouter ellipse si écart > 1 avec la page précédente
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(
                        <span key={`ellipsis-${page}`} className="px-1 sm:px-2 text-xs sm:text-sm text-gray-400">...</span>
                      );
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  
                  return pages;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">📄</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucune facture'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Créez votre première facture'}
          </p>
          {!search && canManage && (
            <Link
              to={`${routeBase}/invoices/new`}
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Créer une facture
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessInvoices;
